import React, { useState } from "react";
import { getReferredByUsers } from "../../../../callable-cloud-functions/cloudFunctions";
import DateGeneric from "../../../custom-ui/old-ui/DateGeneric";
import { csvDownload } from "../../AdminMetricDashboard/ExportCsv";
import DownloadIconWithLabel from "../../AdminMetricDashboard/DownLoadIcon";
import { useLookupActive } from "../../../../data/lookup";
import ReferralDropdown from "./UI/ReferralDropdown";

const ALL_REFERRAL_SOURCE = "ALL";
const RECRUITER_CODE = "REC";

const getSelectableReferralSources = (list: any[]) => {
  const sources: { label: string; value: string }[] = [];
  const seen = new Set<string>();

  const addSource = (label: string, value: string) => {
    if (!value || value === ALL_REFERRAL_SOURCE || seen.has(value)) return;
    seen.add(value);
    sources.push({ label: label || value, value });
  };

  (list || []).forEach((item: any) => {
    if (item?.code === RECRUITER_CODE && item.recruiters?.length) {
      item.recruiters.forEach((rec: any) => addSource(rec?.label, rec?.code));
      return;
    }
    addSource(item?.label, item?.code);
  });

  return sources;
};

const UsersByReferralSource = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [referredBy, setReferredBy] = useState(null);
  const [error, setError] = useState("");
  const [loadingStates, setLoadingStates] = useState({
    loading: false,
  });

  const { data: partners = [] } = useLookupActive("partner");

  const handleDownload = async (loadingKey: string, fileName: string) => {
    setError("");
    setLoadingStates((prev:any) => ({ ...prev, [loadingKey]: true }));

    // 🔹 EXACT same validations as first component
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      setLoadingStates((prev:any) => ({ ...prev, [loadingKey]: false }));
      return;
    }

    if (!referredBy?.value) {
      setError("Please select referral source.");
      setLoadingStates((prev:any) => ({ ...prev, [loadingKey]: false }));
      return;
    }

    if (startDate.getTime() >= endDate.getTime()) {
      setError("Please select a valid date range (start must be before end).");
      setLoadingStates((prev:any) => ({ ...prev, [loadingKey]: false }));
      return;
    }
console.log(referredBy.value)
    try {
      if (referredBy.value === ALL_REFERRAL_SOURCE) {
        const sources = getSelectableReferralSources(partners);
        if (!sources.length) {
          setError("No referral sources are available.");
          return;
        }

        const responses = await Promise.all(
          sources.map((source) =>
            getReferredByUsers({
              startDate: startDate.getTime(),
              endDate: endDate.getTime(),
              referredBy: source.value,
            }),
          ),
        );

        const combined: any[] = [];
        responses.forEach((response) => {
          const { data }: any = response?.data || {};
          if (Array.isArray(data) && data.length) {
            combined.push(...data);
          }
        });

        if (!combined.length) {
          setError("No data found in the selected range.");
          return;
        }

        csvDownload(combined, "users_referredBy_all");
        return;
      }

      const response = await getReferredByUsers({
        startDate: startDate.getTime(),
        endDate: endDate.getTime(),
        referredBy: referredBy.value, // ✅ SAME CONTRACT AS WORKING COMPONENT
      });

      const { data, fileName: fetchedFileName }: any = response.data || {};

      if (!data || !data.length) {
        setError("No data found in the selected range.");
        return;
      }

      csvDownload(data, fetchedFileName || fileName);
    } catch (error) {
      console.error("Error during download:", error);
      setError("Something went wrong while downloading the data.");
    } finally {
      setLoadingStates((prev:any) => ({ ...prev, [loadingKey]: false }));
    }
  };

  return (
    <div className="rounded-lg mt-3 bg-white border p-6 bg-card text-card-foreground shadow-sm">
      <div className="mb-6">
        <p className="text-2xl font-extrabold text-[#1E596D]">
          Users by Referral Source
        </p>
        <p className="text-lg text-instant-teams-teal-Main">
          Generate report for users filtered by referral source and date range
        </p>
      </div>

      {/* Date Range Picker */}
      <div className="gap-2 grid grid-cols-2">
        <div className="w-full">
          <DateGeneric
            name="startDate"
            display="Start Date"
            value={startDate}
            onChange={setStartDate}
            timestamp={true}
            required
            dateLimit="past"
          />
        </div>
        <div className="w-full">
          <DateGeneric
            name="endDate"
            display="End Date"
            value={endDate}
            onChange={setEndDate}
            timestamp={true}
            required
            dateLimit="past"
          />
        </div>
      </div>

      {/* Referral Source */}
      <div>
        <h2 className="text-lg text-[#1E596D] font-bold">Referral Source</h2>
        <ReferralDropdown
          dropDownList={partners}
          selectedValue={referredBy}
          setSelectedValue={setReferredBy}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-3">
          <p className="text-red-500 text-lg">{error}</p>
        </div>
      )}

      {/* Download Button */}
      <div
        onClick={() => handleDownload("loading", "users_referredBy")}
        className="flex cursor-pointer bg-instant-teams-teal-Main py-3 mt-5 rounded-md text-white justify-center items-center"
      >
        {loadingStates.loading ? (
          <p className="text-lg text-white">Downloading...</p>
        ) : (
          <>
            <div className="flex items-start mr-3">
              <DownloadIconWithLabel
                tooltipText="Download users from selected date range and referral source."
                isFromEmpMetric={true}
                viewDown={true}
              />
            </div>
            <p className="text-lg">Download Users by Referral Report</p>
          </>
        )}
      </div>
    </div>
  );
};

export default UsersByReferralSource;
